import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useAuth } from '@/hooks/useAuth'
import { getAuthConfig } from '@/services/auth/config'
import {
  subscribeInsufficientCredits,
  type InsufficientCreditsPayload,
} from '@/services/api/insufficient-credits-emitter'

/**
 * Centralized out-of-credits surface. Mounted once in `AppLayout`, it listens
 * (React-free) for 402 `insufficient_credits` responses emitted by the api
 * client and renders a single role-aware modal. Self-gating:
 *
 * - Renders nothing while no payload is pending (idle).
 * - Renders nothing on-prem (`provider === 'keycloak'`) — billing is cloud-only.
 *
 * The 402 payload's `topupUrl` is informational only; admins are always routed
 * to the in-app `/settings/billing` panel. Non-admins get an "ask your org
 * admin" message and can only dismiss.
 */
export function InsufficientCreditsModal() {
  const { t } = useTranslation('console')
  const navigate = useNavigate()
  const { user } = useAuth()
  const [payload, setPayload] = useState<InsufficientCreditsPayload | null>(null)

  // Subscribe on mount, unsubscribe on unmount.
  useEffect(() => subscribeInsufficientCredits(setPayload), [])

  const isAdmin = user?.role === 'admin'

  const dismiss = () => setPayload(null)

  const goToBilling = () => {
    navigate('/settings/billing')
    dismiss()
  }

  // Close on Escape.
  useEffect(() => {
    if (!payload) return

    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setPayload(null)
    }

    window.addEventListener('keydown', onKey)

    return () => window.removeEventListener('keydown', onKey)
  }, [payload])

  // Cloud-only: on-prem (keycloak) has no billing surface. `getAuthConfig()`
  // throws if it runs before `loadAuthConfig()` has resolved — that only
  // happens at first paint, before any authed call could 402, so treat the
  // not-yet-loaded state as "render nothing" rather than crashing the layout
  // tree this modal is mounted in.
  let provider: string | undefined

  try {
    provider = getAuthConfig().provider
  } catch {
    return null
  }

  if (provider === 'keycloak') return null

  if (!payload) return null

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('billing.outOfCredits.title', 'Out of credits')}
      onClick={dismiss}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-900"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('billing.outOfCredits.title', 'Out of credits')}
        </h2>

        <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
          {t(
            'billing.outOfCredits.body',
            'This action needs more credits than your organization currently has available.',
          )}
        </p>

        <dl className="mt-4 space-y-2 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex items-center justify-between">
            <dt className="text-gray-600 dark:text-gray-400">{t('billing.outOfCredits.shortfall', 'Shortfall')}</dt>
            <dd className="font-semibold text-gray-900 dark:text-gray-100">{payload.shortfallCredits}</dd>
          </div>
          <div className="flex items-center justify-between">
            <dt className="text-gray-600 dark:text-gray-400">{t('billing.outOfCredits.balance', 'Current balance')}</dt>
            <dd className="font-semibold text-gray-900 dark:text-gray-100">{payload.balanceCredits}</dd>
          </div>
        </dl>

        {!isAdmin && (
          <p className="mt-4 text-sm text-gray-700 dark:text-gray-300">
            {t('billing.outOfCredits.askAdmin', 'Ask your organization admin to top up credits.')}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={dismiss}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {t('billing.outOfCredits.dismiss', 'Dismiss')}
          </button>
          {isAdmin && (
            <button
              type="button"
              onClick={goToBilling}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
            >
              {t('billing.outOfCredits.topUp', 'Top up')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
